// UC-3: Variant Mapping — map observed external identifiers to product Variants
//
// Read-only catalog of existing product Variants for an organisation, used
// by SuggestedMatchPicker's fuller search/create picker (spec § Suggested
// match selection: "other product Variants in the merchant / organization
// catalog where needed"). master_products carries no display-name column,
// so the representative label for each Variant is derived from its
// product_identifier_links rows (first non-empty description_text, falling
// back to the identifier value) — documented here as a non-obvious
// Build-time interpretation choice, mirroring useVariantMappingRows.
//
// Post-delivery rework (2026-08-18, Bug D / QA #12): this catalog used to be
// derived *entirely* from active product_identifier_links rows — a variant
// with zero active links (e.g. because write_mapping_confirm's upsert just
// repointed its only identifier onto a different variant) had NO entry at
// all and silently vanished from every picker. The catalog is now enumerated
// from `master_products` itself (every real variant in the org, per HD-4
// clauses 1-3), with product_identifier_links used only to resolve each
// variant's *display identity* when available. HD-4 clause 5 (a variant
// already mapped to some other identifier remains eligible) is enforced via
// `filterEligibleCandidates` — this was previously not enforced by any code
// path at all.
//
// Post-delivery rework (2026-08-19, Bug D remaining defect — Increment 12
// third rework pass): each catalog entry also carries `aliases` — every
// identifier value the variant has ever had, active or inactive (see
// `buildVariantAliasMap`'s docstring for the inactive-inclusion decision) —
// so `SuggestedMatchPicker` can match a search term against ANY of a
// variant's identifiers, not just whichever single link `display_name`/
// `code` collapsed down to.

import { useMemo } from 'react';
import { fetchAllOrgScopedRows, type PagedOrgClient } from './pagedOrgSelect';
import {
  cloneReadFilterFromWorkbench,
  useVariantMappingWorkbench,
} from './useVariantMappingWorkbench';
import {
  inheritedMappingRowVisible,
  type CloneMappingReadFilter,
} from '@/shared/variantMapping/cloneMappingCutoff';
import { buildVariantAliasMap, buildVariantIdentityMap } from './resolveVariantIdentity';
import { filterEligibleCandidates } from './variantEligibility';

export interface VariantCatalogEntry {
  master_product_id: string;
  display_name: string;
  code: string;
  /** Every identifier value this variant carries (active or inactive) — search against this, not just display_name/code. */
  aliases: string[];
  /** Currently active identifier values. Empty for emptied shells that only have last_* snapshots. */
  activeIdentifiers?: string[];
  /** UC-174: master_products.eve_id (integer). Displayed as EVE-<n>. */
  eve_id: number;
  /** UC-174: description_text from links when present; null renders as "—". */
  product_description: string | null;
  /** UC-174: count of active identifier associations on this Variant. */
  linked_identifier_count: number;
  /** Active identifier links, used to fill the Variants Manager matrix. */
  linked_identifiers: Array<{ type: string; value: string; linkId?: string }>;
}

export interface PickerTarget {
  master_product_id: string;
  display_name: string;
  code: string;
}

interface LinkRow {
  master_product_id: string;
  identifier_value: string;
  identifier_type?: string | null;
  description_text?: string | null;
  active: boolean;
  id?: string;
  created_at?: string;
}

interface MasterProductRow {
  id: string;
  organisation_id: string;
  eve_id: number;
  /** Snapshot written when the last active identifier leaves this variant (repoint). */
  last_display_name?: string | null;
  last_identifier_value?: string | null;
  /** UC-176: non-null when this master was merged into a survivor and hidden. */
  merged_into_id?: string | null;
}

export type VariantCatalogClient = PagedOrgClient;

export function assembleVariantCatalog(
  masterProducts: MasterProductRow[],
  linksInput: LinkRow[],
  organisationId: string,
  cloneReadFilter?: CloneMappingReadFilter,
): VariantCatalogEntry[] {
  // UC-176: hide masters marked merged from the active Variants Manager table.
  const variants = masterProducts.filter((v) => !v.merged_into_id);
  const links = linksInput;
  const identityMap = buildVariantIdentityMap(links);
  const aliasMap = buildVariantAliasMap(links);
  const descriptionMap = buildProductDescriptionMap(links);

  const eligibleVariants = filterEligibleCandidates(
    variants.map((v) => ({ master_product_id: v.id, organisation_id: v.organisation_id })),
    organisationId,
  );

  const variantById = new Map(variants.map((v) => [v.id, v]));
  const activeIdentifiersByMaster = new Map<string, string[]>();
  const linkedIdentifiersByMaster = new Map<
    string,
    Array<{ type: string; value: string; linkId?: string }>
  >();
  const occupiedIdentifiers = new Set<string>();
  for (const link of links) {
    if (!link.active) continue;
    const value = link.identifier_value?.trim();
    if (!value) continue;
    occupiedIdentifiers.add(value);
    const list = activeIdentifiersByMaster.get(link.master_product_id) ?? [];
    list.push(value);
    activeIdentifiersByMaster.set(link.master_product_id, list);
    const type = link.identifier_type?.trim();
    if (!type) continue;
    // The report matrix is built from these links. CSV export drops inherited
    // rows past the clone cutoff, so the on-screen Sold as / Bought as values
    // must use the same filter. Picker identity still sees every active link.
    if (
      cloneReadFilter &&
      !inheritedMappingRowVisible(link.created_at, cloneReadFilter.orgCreatedAt, cloneReadFilter.cutoffDate)
    ) {
      continue;
    }
    const typed = linkedIdentifiersByMaster.get(link.master_product_id) ?? [];
    typed.push({ type, value, linkId: link.id });
    linkedIdentifiersByMaster.set(link.master_product_id, typed);
  }
  const mastersWithActiveLinks = new Set(activeIdentifiersByMaster.keys());

  const catalog = eligibleVariants.map(({ master_product_id }) => {
    const identity = identityMap.get(master_product_id);
    const snapshot = variantById.get(master_product_id);
    const snapshotCode = snapshot?.last_identifier_value?.trim() || '';
    const snapshotName = snapshot?.last_display_name?.trim() || snapshotCode;
    const display_name = identity?.display_name || snapshotName || 'Unmapped variant';
    const code = identity?.code || snapshotCode || shortUnmappedCode(master_product_id);
    const activeIdentifiers = activeIdentifiersByMaster.get(master_product_id) ?? [];
    const aliases = new Set(aliasMap.get(master_product_id) ?? []);
    // last_* is only a leftover label. Once this product has live codes,
    // do not keep a departed SKU searchable on it (SHOP leftover must not
    // match a search for B0BLEND that now lives elsewhere).
    if (snapshotCode && activeIdentifiers.length === 0) aliases.add(snapshotCode);
    return {
      master_product_id,
      display_name,
      code,
      aliases: Array.from(aliases),
      activeIdentifiers,
      eve_id: snapshot?.eve_id ?? 0,
      product_description: descriptionMap.get(master_product_id) ?? null,
      linked_identifier_count: activeIdentifiers.length,
      linked_identifiers: linkedIdentifiersByMaster.get(master_product_id) ?? [],
    };
  });

  // Emptied UUID shells accumulate on rematch/create-new. Hide ghosts whose
  // leftover SKU is already active on a live product, and collapse those
  // leftovers by last identifier. Link-less masters stay, one per EVE id.
  const live = catalog.filter((entry) => mastersWithActiveLinks.has(entry.master_product_id));
  const uniqueEmptied = uniqueEmptiedProducts(
    catalog.filter((entry) => !mastersWithActiveLinks.has(entry.master_product_id)),
    occupiedIdentifiers,
  );

  return [...live, ...uniqueEmptied].sort((a, b) => {
    const aUnmapped = a.display_name === 'Unmapped variant';
    const bUnmapped = b.display_name === 'Unmapped variant';
    if (aUnmapped !== bUnmapped) return aUnmapped ? 1 : -1;
    return a.display_name.localeCompare(b.display_name);
  });
}

export async function fetchVariantCatalog(
  client: VariantCatalogClient,
  organisationId: string,
  pageSize?: number,
  cloneReadFilter?: CloneMappingReadFilter,
): Promise<VariantCatalogEntry[]> {
  const [variantsResult, linksResult] = await Promise.all([
    fetchAllOrgScopedRows<MasterProductRow>(
      client,
      'master_products',
      'id, organisation_id, eve_id, last_display_name, last_identifier_value, merged_into_id',
      organisationId,
      pageSize,
    ),
    fetchAllOrgScopedRows<LinkRow>(client, 'product_identifier_links', '*', organisationId, pageSize),
  ]);

  if (variantsResult.error) throw new Error(variantsResult.error.message);
  if (linksResult.error) throw new Error(linksResult.error.message);

  return assembleVariantCatalog(
    variantsResult.data ?? [],
    linksResult.data ?? [],
    organisationId,
    cloneReadFilter,
  );
}

function uniqueEmptiedProducts(
  emptied: VariantCatalogEntry[],
  occupiedIdentifiers: ReadonlySet<string>,
): VariantCatalogEntry[] {
  const byCode = new Map<string, VariantCatalogEntry>();
  const unnamed: VariantCatalogEntry[] = [];

  for (const entry of [...emptied].sort((a, b) => a.master_product_id.localeCompare(b.master_product_id))) {
    // Each link-less master is its own EVE id. Collapsing them hid every
    // product that has not been mapped yet.
    if (entry.display_name === 'Unmapped variant') {
      unnamed.push(entry);
      continue;
    }
    const key = entry.code.trim();
    if (!key) continue;
    const aliasesOccupied = entry.aliases.some((alias) => occupiedIdentifiers.has(alias.trim()));
    if (occupiedIdentifiers.has(key) || aliasesOccupied) continue;
    if (byCode.has(key)) continue;
    byCode.set(key, entry);
  }

  return [...byCode.values(), ...unnamed];
}

/**
 * Picker rows can be emptied shells labeled with last_*. If that identifier
 * is already active on a live product, attach to the live product instead of
 * the husk — otherwise confirm writes the source SKU onto a ghost UUID and
 * the matched column flips to the source SKU.
 */
export function resolvePickerTarget(
  candidate: PickerTarget,
  catalog: readonly Pick<VariantCatalogEntry, 'master_product_id' | 'display_name' | 'code' | 'activeIdentifiers'>[],
): PickerTarget {
  const selectedCode = candidate.code.trim();
  if (!selectedCode) return candidate;

  const liveOwner = catalog.find((entry) =>
    (entry.activeIdentifiers ?? []).some((value) => value.trim() === selectedCode),
  );
  if (!liveOwner) return candidate;

  return {
    master_product_id: liveOwner.master_product_id,
    display_name: liveOwner.display_name,
    code: liveOwner.code,
  };
}

export interface SurvivingPickerTarget extends PickerTarget {
  absorbFromMasterProductId?: string;
}

/**
 * Confirm/stage only: leftover one-code products are absorbed into the
 * product that already owns the row identifier. Pencil edit does not use this.
 */
export function resolveSurvivingPickerTarget(input: {
  sourceIdentifier: string;
  candidate: PickerTarget;
  catalog: readonly Pick<VariantCatalogEntry, 'master_product_id' | 'display_name' | 'code' | 'activeIdentifiers'>[];
}): SurvivingPickerTarget {
  const clicked = resolvePickerTarget(input.candidate, input.catalog);
  const source = input.sourceIdentifier.trim();
  const sourceHome = input.catalog.find((entry) =>
    (entry.activeIdentifiers ?? []).some((value) => value.trim() === source),
  );
  const picked = input.catalog.find((entry) => entry.master_product_id === clicked.master_product_id);
  const pickedIsThin =
    picked?.activeIdentifiers !== undefined && picked.activeIdentifiers.length <= 1;
  const pickedIsEmptyShell =
    picked?.activeIdentifiers !== undefined && picked.activeIdentifiers.length === 0;

  if (sourceHome && pickedIsThin && sourceHome.master_product_id !== clicked.master_product_id) {
    return {
      master_product_id: sourceHome.master_product_id,
      display_name: sourceHome.display_name,
      code: sourceHome.code,
      absorbFromMasterProductId: clicked.master_product_id,
    };
  }

  // Empty husks only: a live one-code product (just created on another row)
  // must keep its existing display identity when this unmatched row maps onto it.
  if (!sourceHome && pickedIsEmptyShell && source) {
    return {
      master_product_id: clicked.master_product_id,
      display_name: source,
      code: source,
    };
  }

  return clicked;
}

export function matchingPickerCode(
  entry: Pick<VariantCatalogEntry, 'code' | 'aliases' | 'activeIdentifiers'>,
  search: string,
): string {
  const query = search.trim().toLowerCase();
  const pool =
    (entry.activeIdentifiers?.length ?? 0) > 0 ? (entry.activeIdentifiers ?? []) : (entry.aliases ?? []);
  if (!query) return entry.code;
  const match = pool.find((alias) => alias.toLowerCase().includes(query));
  return match ?? entry.code;
}

function shortUnmappedCode(masterProductId: string): string {
  const compact = masterProductId.replace(/-/g, '');
  return compact.slice(0, 8);
}

/** First non-empty description_text wins (active links first, then inactive), deterministic order. */
function buildProductDescriptionMap(links: LinkRow[]): Map<string, string> {
  const byVariant = new Map<string, string>();
  const sorted = [...links].sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (aTime !== bTime) return aTime - bTime;
    return String(a.id ?? '').localeCompare(String(b.id ?? ''));
  });
  for (const link of sorted) {
    if (!link.active) continue;
    if (byVariant.has(link.master_product_id)) continue;
    const text = link.description_text?.trim();
    if (text) byVariant.set(link.master_product_id, text);
  }
  for (const link of sorted) {
    if (link.active) continue;
    if (byVariant.has(link.master_product_id)) continue;
    const text = link.description_text?.trim();
    if (text) byVariant.set(link.master_product_id, text);
  }
  return byVariant;
}

export function useVariantCatalog(organisationId: string | undefined) {
  const workbench = useVariantMappingWorkbench(organisationId);
  const data = useMemo(() => {
    if (!workbench.data || !organisationId) return undefined;
    return assembleVariantCatalog(
      workbench.data.master_products,
      workbench.data.links,
      organisationId,
      cloneReadFilterFromWorkbench(workbench.data.organisation),
    );
  }, [organisationId, workbench.data]);
  return { ...workbench, data };
}
